import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePrimaryOrganization, useSelf } from "../../hooks";
import { BUSINESS_SURVEY_NAME, businessSurvey } from "../../surveys/businessSurvey";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { hasAnswered, questionsWithVisibility, QuestionWithVisibility } from "../../surveys/survey";
import { SurveyQuestion } from "../../components/get-compliant/survey/SurveyQuestion";
import { PageLoading } from "../../components";
import { Button, Modal, OnboardingOrganizationDropdown, useSnackBar } from "@truevault/aquilo";
import clsx from "clsx";
import { useUpdateDataSources } from "../get-compliant/data-map/ConsumerDataSourcesPage";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { HR_SURVEY_NAME, hrSurvey } from "../../surveys/hrSurvey";
import { FlagIcon } from "@heroicons/react/24/outline";
import { FlagIcon as FlagIconFilled, CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  EEA_UK_SURVEY_NAME,
  eeaUkSurvey,
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "../../surveys/eeaUkSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  consumerCollectionIncentivesSurvey,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../surveys/consumerCollectionIncentivesSurvey";
import {
  PrivacyCenterSettingsProps,
  usePrivacyCenterSettings,
} from "../../components/superorg/settings/PrivacyCenterSettings";
import { DpoValues, EditDpoDialog } from "../../components/dialog/EditDpoDialog";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { PrivacyCenterPipedaPrivacyOfficerRow } from "../../components/organisms/settings/rows/PrivacyCenterPipedaPrivacyOfficerRow";
import { filterVendorsForDataSourceSurveyQuestion } from "../../components/get-compliant/survey/custom/AddDataSourceQuestion";
import { EditEeaContactDialog, EeaContactValues } from "../../components/dialog/EEAContactDialog";
import { Api } from "../../../common/service/Api";
import { SurveyBooleanQuestionProps } from "../../components/get-compliant/survey/SurveyBooleanQuestion";
import { PrivacyCenterDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { OmniBar } from "../../components/dialog/OmniBar";
import { OmniBarProvider } from "../../../common/hooks/useOmniBar";

type CurrentView =
  | "business-survey-welcome"
  | "business-survey"
  // Skip welcome screen for incentives survey since its just currently one question
  | "consumer-collection-incentives-survey"
  | "eea-uk-survey-welcome"
  | "eea-uk-survey"
  | "pipeda-privacy-officer"
  | "hr-survey"
  | "finished-review-modal";

export function BusinessSurveyAquiloPage() {
  const showSnackbar = useSnackBar();

  const [org] = usePrimaryOrganization();
  const [self] = useSelf();

  const [currentView, setCurrentView] = useState<CurrentView>(null);

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings();

  const {
    answers: bizAnswers,
    setAnswer: setBizAnswer,
    remote: bizSurveyRemote,
    flaggedForReviewSlugs: bizFlaggedForReviewSlugs,
  } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);

  const {
    answers: incentivesAnswers,
    setAnswer: setIncentivesAnswer,
    remote: incentivesSurveyRemote,
    flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs,
  } = useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME);

  const updateDataSources = useUpdateDataSources(org?.id);

  const {
    answers: hrAnswers,
    setAnswer: setHrAnswer,
    remote: hrRemote,
    flaggedForReviewSlugs: hrFlaggedForReviewSlugs,
  } = useSurvey(org?.id, HR_SURVEY_NAME);

  const {
    answers: eeaAnswers,
    setAnswer: setEeaAnswer,
    remote: eeaRemote,
    flaggedForReviewSlugs: eeaFlaggedForReviewSlugs,
  } = useSurvey(org?.id, EEA_UK_SURVEY_NAME);

  const remoteBizSurveyAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(
        Object.values(bizSurveyRemote?.answers ?? {}).map((q) => [q.slug, q.answer]),
      ),
    [bizSurveyRemote],
  );

  const remoteIncentivesAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(
        Object.values(incentivesSurveyRemote?.answers ?? {}).map((q) => [q.slug, q.answer]),
      ),
    [incentivesSurveyRemote],
  );

  const remoteHrAnswers = useMemo<SurveyAnswers>(
    () => Object.fromEntries(Object.values(hrRemote?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [hrRemote],
  );

  const remoteEeaAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(Object.values(eeaRemote?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [eeaRemote],
  );

  // DPO
  const isDpoRequired = org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter);
  const hasDpo = Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
  const [isEditDpoDialogOpen, setIsEditDpoDialogOpen] = useState(false);

  // Designated Representative
  const hasDesignatedRep = hasDesignatedRepresentative(pcSettings);
  const [isDesignatedRepDialogOpen, setIsDesignatedRepDialogOpen] = useState(false);

  const [currentQuestionSlug, setCurrentQuestionSlug] = useState<string | null>(null);

  const allFlaggedForReviewSlugs = useMemo(
    () => [
      ...bizFlaggedForReviewSlugs,
      ...incentivesFlaggedForReviewSlugs,
      ...hrFlaggedForReviewSlugs,
      ...eeaFlaggedForReviewSlugs,
    ],
    [
      bizFlaggedForReviewSlugs,
      incentivesFlaggedForReviewSlugs,
      hrFlaggedForReviewSlugs,
      eeaFlaggedForReviewSlugs,
    ],
  );

  useEffect(() => {
    if (
      currentView === null &&
      org &&
      privacyCenter &&
      bizAnswers &&
      Object.keys(bizAnswers).length > 0 &&
      incentivesAnswers &&
      Object.keys(incentivesAnswers).length > 0 &&
      (!org?.featureGdpr || (eeaAnswers && Object.keys(eeaAnswers).length > 0)) &&
      hrAnswers &&
      Object.keys(hrAnswers).length > 0 &&
      updateDataSources &&
      pcSettings
    ) {
      let [initialView, initialQuestionSlug] = initialViewAndQuestionSlug(
        org,
        { bizAnswers, hrAnswers, eeaAnswers, incentivesAnswers },
        privacyCenter,
        pcSettings,
        allFlaggedForReviewSlugs,
      );
      setCurrentView(initialView);
      setCurrentQuestionSlug(initialQuestionSlug);
    }
  }, [
    org,
    bizAnswers,
    incentivesAnswers,
    incentivesSurveyRemote,
    eeaAnswers,
    hrAnswers,
    allFlaggedForReviewSlugs,
    pcSettings,
    updateDataSources,
    isDpoRequired,
    hasDpo,
    currentView,
  ]);

  let {
    bizQuestionsWithVisibility,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
    bizAnswers,
    hrAnswers,
    eeaAnswers,
    incentivesAnswers,
  });

  let allQuestionsWithVisibility = [
    ...bizQuestionsWithVisibility,
    ...incentivesQuestionsWithVisibility,
    ...hrQuestionsWithVisibility,
    ...eeaQuestionsWithVisibility,
  ];

  const surveyDataByView: Partial<
    Record<
      CurrentView,
      {
        currentAnswers: SurveyAnswers;
        currentRemoteAnswers: SurveyAnswers;
        currentSetAnswer: SurveyAnswerFn;
        currentQuestionsVisibilityWithDefault: QuestionWithVisibility[];
      }
    >
  > = {
    "business-survey": {
      currentAnswers: bizAnswers,
      currentRemoteAnswers: remoteBizSurveyAnswers,
      currentSetAnswer: setBizAnswer,
      currentQuestionsVisibilityWithDefault: bizQuestionsWithVisibility,
    },
    [CONSUMER_INCENTIVES_SURVEY_NAME]: {
      currentAnswers: incentivesAnswers,
      currentRemoteAnswers: remoteIncentivesAnswers,
      currentSetAnswer: setIncentivesAnswer,
      currentQuestionsVisibilityWithDefault: incentivesQuestionsWithVisibility,
    },
    "hr-survey": {
      currentAnswers: hrAnswers,
      currentRemoteAnswers: remoteHrAnswers,
      currentSetAnswer: setHrAnswer,
      currentQuestionsVisibilityWithDefault: hrQuestionsWithVisibility,
    },
    "eea-uk-survey": {
      currentAnswers: eeaAnswers,
      currentRemoteAnswers: remoteEeaAnswers,
      currentSetAnswer: setEeaAnswer,
      currentQuestionsVisibilityWithDefault: eeaQuestionsWithVisibility,
    },
  };

  let {
    currentQuestionsVisibilityWithDefault,
    currentAnswers,
    currentRemoteAnswers,
    currentSetAnswer,
  } = surveyDataByView[currentView] || {};
  let visibleCurrentQuestions = currentQuestionsVisibilityWithDefault?.filter((q) => q.visible);

  let currentQuestion = allQuestionsWithVisibility?.find((q) => q.slug === currentQuestionSlug);
  let currentQuestionText = currentQuestion?.plaintextQuestion;
  if (!currentQuestionText && typeof currentQuestion?.question == "string") {
    currentQuestionText = currentQuestion?.question;
  }

  const moveToPrevQuestion = useCallback(() => {
    if (currentView === "eea-uk-survey-welcome") {
      setCurrentView("consumer-collection-incentives-survey");
      let visibleIncentiveQuestions = incentivesQuestionsWithVisibility.filter((q) => q.visible);
      setCurrentQuestionSlug(visibleIncentiveQuestions[visibleIncentiveQuestions.length - 1].slug);
      return;
    } else if (currentView === "pipeda-privacy-officer") {
      if (org?.featureGdpr) {
        setCurrentView("eea-uk-survey");
        let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleEeaQuestions[visibleEeaQuestions.length - 1].slug);
      } else {
        // setCurrentView("consumer-sources-survey");
        // let visibleSourcesQuestions = sourcesQuestionsWithVisibility.filter((q) => q.visible);
        // setCurrentQuestionSlug(visibleSourcesQuestions[visibleSourcesQuestions.length - 1].slug);
      }
      return;
    }

    let idx = visibleCurrentQuestions.findIndex((q) => q.slug === currentQuestionSlug);
    let prevVisibleIdx = idx - 1;
    while (prevVisibleIdx >= 0 && !visibleCurrentQuestions[prevVisibleIdx].visible) {
      prevVisibleIdx--;
    }

    if (prevVisibleIdx >= 0) {
      setCurrentQuestionSlug(visibleCurrentQuestions[prevVisibleIdx].slug);
    } else {
      if (currentView === "business-survey") {
        setCurrentView("business-survey-welcome");
      } else if (currentView === "consumer-collection-incentives-survey") {
        setCurrentView("business-survey");
        let visibleBizQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleBizQuestions[visibleBizQuestions.length - 1].slug);
      } else if (currentView === "eea-uk-survey") {
        setCurrentView("eea-uk-survey-welcome");
      } else if (currentView === "hr-survey") {
        if (org.featurePipeda) {
          setCurrentView("pipeda-privacy-officer");
        } else if (org.featureGdpr) {
          setCurrentView("eea-uk-survey");
          let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
          setCurrentQuestionSlug(visibleEeaQuestions[visibleEeaQuestions.length - 1].slug);
        } else {
          setCurrentView("consumer-collection-incentives-survey");
          let visibleIncentiveQuestions = incentivesQuestionsWithVisibility.filter(
            (q) => q.visible,
          );
          setCurrentQuestionSlug(
            visibleIncentiveQuestions[visibleIncentiveQuestions.length - 1].slug,
          );
        }
      }
    }
  }, [
    bizQuestionsWithVisibility,
    currentQuestionSlug,
    currentView,
    eeaQuestionsWithVisibility,
    isDpoRequired,
    org,
    visibleCurrentQuestions,
  ]);

  const moveToNextQuestion = useCallback(
    (updatedAnswers: SurveyAnswers) => {
      if (currentView === "eea-uk-survey-welcome") {
        setCurrentView("eea-uk-survey");
        let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleEeaQuestions[0].slug);
        return;
      }

      // Re-calculate the survey answers with the latest `updatedAnswers`
      let allUpdatedAnswers = { bizAnswers, incentivesAnswers, eeaAnswers, hrAnswers };
      if (currentView === "business-survey") {
        allUpdatedAnswers.bizAnswers = updatedAnswers;
      } else if (currentView === "consumer-collection-incentives-survey") {
        allUpdatedAnswers.incentivesAnswers = updatedAnswers;
      } else if (currentView === "eea-uk-survey") {
        allUpdatedAnswers.eeaAnswers = updatedAnswers;
      } else if (currentView === "hr-survey") {
        allUpdatedAnswers.hrAnswers = updatedAnswers;
      }

      // Re-calculate survey questions visibliity with new survey answers
      let {
        bizQuestionsWithVisibility: newBizQuestionsWithVisibility,
        incentivesQuestionsWithVisibility: newIncentivesQuestionsWithVisibility,
        hrQuestionsWithVisibility: newHrQuestionsWithVisibility,
        eeaQuestionsWithVisibility: newEeaQuestionsWithVisibility,
      } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, allUpdatedAnswers);

      let visibleBizQuestions = newBizQuestionsWithVisibility.filter((q) => q.visible);
      let visibleIncentivesQuestions = newIncentivesQuestionsWithVisibility.filter(
        (q) => q.visible,
      );
      let visibleHrQuestions = newHrQuestionsWithVisibility.filter((q) => q.visible);
      let visibleEeaQuestions = newEeaQuestionsWithVisibility.filter((q) => q.visible);

      // Get the current set of visible questions
      let currentVisibleQuestions = {
        "business-survey": visibleBizQuestions,
        "consumer-collection-incentives-survey": visibleIncentivesQuestions,
        "hr-survey": visibleHrQuestions,
        "eea-uk-survey": visibleEeaQuestions,
      }[currentView];

      if (currentView === "pipeda-privacy-officer") {
        setCurrentView("hr-survey");
        setCurrentQuestionSlug(visibleHrQuestions[0].slug);
        return;
      }

      // Find the index of the next question within the current set of visible questions, or move to the next view
      let idx = currentVisibleQuestions.findIndex((q) => q.slug === currentQuestionSlug);
      let nextVisibleIdx = idx + 1;
      while (
        nextVisibleIdx < currentVisibleQuestions.length &&
        !currentVisibleQuestions[nextVisibleIdx].visible
      ) {
        nextVisibleIdx++;
      }
      if (nextVisibleIdx < currentVisibleQuestions.length) {
        setCurrentQuestionSlug(currentVisibleQuestions[nextVisibleIdx].slug);
      } else {
        if (currentView == "business-survey") {
          setCurrentView("consumer-collection-incentives-survey");
          setCurrentQuestionSlug(visibleIncentivesQuestions[0].slug);
        } else if (currentView === "consumer-collection-incentives-survey") {
          if (org?.featureGdpr) {
            setCurrentView("eea-uk-survey-welcome");
          } else if (org.featurePipeda) {
            setCurrentView("pipeda-privacy-officer");
          } else {
            setCurrentView("hr-survey");
            setCurrentQuestionSlug(visibleHrQuestions[0].slug);
          }
        } else if (currentView === "eea-uk-survey") {
          if (org.featurePipeda) {
            setCurrentView("pipeda-privacy-officer");
          } else {
            setCurrentView("hr-survey");
            setCurrentQuestionSlug(visibleHrQuestions[0].slug);
          }
        } else if (currentView === "hr-survey") {
          setCurrentView("finished-review-modal");
        }
      }
    },
    [
      bizAnswers,
      incentivesAnswers,
      eeaAnswers,
      hrAnswers,
      currentView,
      org,
      allFlaggedForReviewSlugs,
      pcSettings,
      updateDataSources,
      currentQuestionSlug,
      isDpoRequired,
    ],
  );

  if (!org || currentView === null) {
    return <PageLoading />;
  }

  let isShowingWelcomeMessage =
    currentView === "business-survey-welcome" || currentView === "eea-uk-survey-welcome";
  let isShowingSurvey =
    currentView === "business-survey" ||
    currentView === "consumer-collection-incentives-survey" ||
    currentView === "eea-uk-survey" ||
    currentView === "hr-survey";

  let isPrevVisible = currentView !== "business-survey-welcome";
  let isNextVisible = currentView !== "business-survey-welcome";
  let isNextEnabled = (() => {
    if (isShowingSurvey) {
      // For data source questions, enable the next button after a relevant vendor has been added
      if (currentQuestion && currentQuestion.type === "custom-add-data-source") {
        let filteredVendors = filterVendorsForDataSourceSurveyQuestion(currentQuestion);
        let filteredVendorIds = new Set(filteredVendors.map((v) => v.id));
        return (
          (currentQuestion?.sources || []).filter((s) => filteredVendorIds.has(s.vendorId)).length >
          0
        );
      }

      // For the DPO question, enable next button if answer is No or if answer is Yes and DPO details have been added
      if (currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG) {
        return (
          currentRemoteAnswers[currentQuestion.slug] === "false" ||
          (currentRemoteAnswers[currentQuestion.slug] === "true" && hasDpo)
        );
      }

      // For the Designated Rep question, enable next button if answer is No or if answer is Yes and EEA/UK contact details have been added
      if (currentQuestion.slug === HAS_DESIGNATED_REP_SLUG) {
        return (
          currentRemoteAnswers[currentQuestion.slug] === "false" ||
          (currentRemoteAnswers[currentQuestion.slug] === "true" && hasDesignatedRep)
        );
      }

      return (
        hasAnswered(currentRemoteAnswers, currentQuestion, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(currentQuestionSlug)
      );
    }

    if (currentView === "pipeda-privacy-officer") {
      return pcSettings?.pipedaOfficerName?.length > 0;
    }

    return isShowingWelcomeMessage;
  })();

  return (
    <OmniBarProvider>
      <OmniBar></OmniBar>
      <div className="tw-boundary">
        <div className="min-h-screen flex" style={{ flexDirection: "column" }}>
          <div
            className={clsx(
              "top-0 sticky",
              "h-[64px] w-full",
              "bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
              "px-[32px] py-[16px] flex items-center justify-between",
            )}
          >
            <div>
              <img
                src="/assets/images/truevault-logo.png"
                className="w-[145px] h-[32px]"
                alt="TrueVault Logo"
              />
            </div>
            <CurrentSurveyIndicator currentView={currentView} isGdprRequired={org?.featureGdpr} />
            <div>
              <OnboardingOrganizationDropdown
                orgName={org.name}
                userDisplayName={`${self.firstName} ${self.lastName}`}
              />
            </div>
          </div>
          <div className="flex-1 flex items-center mt-[120px]" style={{ flexDirection: "column" }}>
            <div
              className={clsx(
                "w-[890px]",
                "rounded-[24px] bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)] border border-solid border-neutral-300",
                "overflow-y-auto",
                "p-[39px]",
                "grid place-items-center",
              )}
            >
              {isShowingWelcomeMessage && (
                <WelcomeMessage
                  currentView={currentView}
                  isGdprRequired={org?.featureGdpr}
                  onNextClick={() => {
                    if (currentView == "business-survey-welcome") {
                      setCurrentView("business-survey");
                      let visibleBizQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
                      setCurrentQuestionSlug(visibleBizQuestions[0].slug);
                    } else if (currentView === "eea-uk-survey-welcome") {
                      setCurrentView("eea-uk-survey");
                      let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
                      setCurrentQuestionSlug(visibleEeaQuestions[0].slug);
                    }
                  }}
                />
              )}
              {isShowingSurvey &&
                (currentQuestion ? (
                  <div>
                    <SurveyQuestion
                      key={currentQuestion.slug}
                      question={(() => {
                        let result = currentQuestion;
                        // For the DPO question, disable the "No" option if DPO is already set since we don't let users remove a DPO after it's been added (at least from the survey)
                        if (currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG && hasDpo) {
                          (result as SurveyBooleanQuestionProps).noDisabled = true;
                          (result as SurveyBooleanQuestionProps).noDisabledTooltip =
                            "You have already added a DPO.";
                        }
                        return result;
                      })()}
                      answer={
                        currentQuestion.visible ? currentAnswers[currentQuestion.slug] : undefined
                      }
                      onAnswer={async (ans) => {
                        let updatedAnswers = await currentSetAnswer(
                          currentQuestion.slug,
                          ans,
                          currentQuestionText,
                          /* flaggedForReview = */ allFlaggedForReviewSlugs.includes(
                            currentQuestion.slug,
                          ),
                        );

                        if (currentQuestion.slug === HAS_DESIGNATED_REP_SLUG) {
                          if (ans === "true") {
                            setIsDesignatedRepDialogOpen(true);
                          } else {
                            await Api.tasks.createEeaDesignatedRepresentativeTask(org.id);
                          }
                          // Return early without auto-advancing so the user can either see the dialog or the task created info message
                          return;
                        }

                        // If they answered "Yes" to the DPO question, open the DPO dialog (and if they answer "No", proceed to next question like normal)
                        if (
                          currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG &&
                          ans === "true"
                        ) {
                          setIsEditDpoDialogOpen(true);
                          return;
                        }

                        // for boolean questions, auto-advance to the next question after the user answers
                        if (currentQuestion.type === "boolean" && updatedAnswers) {
                          moveToNextQuestion(updatedAnswers);
                        }
                      }}
                      loading={
                        currentAnswers[currentQuestion.slug] !=
                        currentRemoteAnswers[currentQuestion.slug]
                      }
                      useNewDesign={true}
                    />
                    {currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG && hasDpo && (
                      <SurveyQuestionSuccessMessage
                        messageText={
                          <div>
                            <div>DPO details were added:</div>
                            <div>{pcSettings.dpoName}</div>
                            <div>{pcSettings.dpoEmail}</div>
                            {pcSettings.dpoPhone && <div>{pcSettings.dpoPhone}</div>}
                            {pcSettings.dpoAddress && <div>{pcSettings.dpoAddress}</div>}
                          </div>
                        }
                      />
                    )}
                    {currentQuestion.slug === HAS_DESIGNATED_REP_SLUG &&
                      currentAnswers[currentQuestion.slug] === "false" && (
                        <SurveyQuestionSuccessMessage messageText="We've added an item on your to-do list to help you set up a Designated Representative for your business in the EEA/UK." />
                      )}
                    {currentQuestion.slug === HAS_DESIGNATED_REP_SLUG &&
                      currentAnswers[currentQuestion.slug] === "true" &&
                      hasDesignatedRep && (
                        <SurveyQuestionSuccessMessage
                          messageText={
                            <div>
                              <div>Designated Representative details were added:</div>
                              <div>{pcSettings.eeaContactName}</div>
                              <div>{pcSettings.eeaContactEmail}</div>
                              <div>{pcSettings.eeaContactAddress}</div>
                              {pcSettings.eeaContactPhone && (
                                <div>{pcSettings.eeaContactPhone}</div>
                              )}
                            </div>
                          }
                        />
                      )}
                    <div
                      className="flex items-center justify-end mt-[27px] cursor-pointer"
                      onClick={async () => {
                        let newIsFlaggedForReview = !allFlaggedForReviewSlugs.includes(
                          currentQuestion.slug,
                        );
                        await currentSetAnswer(
                          currentQuestion.slug,
                          currentAnswers[currentQuestion.slug],
                          currentQuestionText,
                          /* flaggedForReview = */ newIsFlaggedForReview,
                        );

                        if (newIsFlaggedForReview === true) {
                          showSnackbar("Marked for review.", { variant: "info" });
                        } else {
                          showSnackbar("Unmarked for review.", { variant: "info" });
                        }
                      }}
                    >
                      <div>
                        {allFlaggedForReviewSlugs.includes(currentQuestion.slug) ? (
                          <FlagIconFilled className="w-[24px] h-[24px] text-primary-500" />
                        ) : (
                          <FlagIcon className="w-[24px] h-[24px] text-neutral-700" />
                        )}
                      </div>
                      <div
                        className={clsx(
                          "ml-[12px] text-[14px] font-medium",
                          allFlaggedForReviewSlugs.includes(currentQuestion.slug)
                            ? "text-primary-500"
                            : "text-neutral-700",
                        )}
                      >
                        {allFlaggedForReviewSlugs.includes(currentQuestion.slug)
                          ? "Remove flag"
                          : "Flag for Review"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>No question for slug {currentQuestionSlug}</div>
                ))}
              {currentView === "pipeda-privacy-officer" && (
                <div>
                  <div className="text-[30px] font-bold text-neutral-800 text-left">
                    Canadian Data Practices
                  </div>
                  <div className="mt-[24px]">
                    <PrivacyCenterPipedaPrivacyOfficerRow
                      organization={pcSettings.organization}
                      vertical
                      useNewDesign
                      dpoName={pcSettings.dpoName}
                      dpoEmail={pcSettings.dpoEmail}
                      dpoPhone={pcSettings.dpoPhone}
                      dpoAddress={pcSettings.dpoAddress}
                      pipedaOfficerName={pcSettings.pipedaOfficerName}
                      pipedaOfficerEmail={pcSettings.pipedaOfficerEmail}
                      pipedaOfficerPhone={pcSettings.pipedaOfficerPhone}
                      pipedaOfficerAddress={pcSettings.pipedaOfficerAddress}
                      handleFormSubmit={pcSettings.handleFormSubmit}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="w-[825px] h-[38px] mt-[28px] flex items-center justify-between">
              <div>
                {isPrevVisible && (
                  <Button
                    color="secondary"
                    onClick={() => {
                      moveToPrevQuestion();
                    }}
                  >
                    Previous Question
                  </Button>
                )}
              </div>
              <div>
                {isNextVisible && (
                  <Button
                    color="secondary"
                    disabled={!isNextEnabled}
                    onClick={async () => {
                      // For data source questions, also set the answer to "true" (the added vendors are stored separately from the answer)
                      if (
                        isShowingSurvey &&
                        currentQuestion.type === "custom-add-data-source" &&
                        currentAnswers[currentQuestion.slug] !== "true"
                      ) {
                        let updatedAnswers = await currentSetAnswer(
                          currentQuestion.slug,
                          "true",
                          currentQuestionText,
                          /* flaggedForReview = */ allFlaggedForReviewSlugs.includes(
                            currentQuestion.slug,
                          ),
                        );

                        // It's fine to fallback to using the current answers (which won't reflect setting the current question to "true") since there is no survey question whose visibility is dependent on the answer to a data sources question
                        moveToNextQuestion(updatedAnswers || currentAnswers);
                      } else {
                        moveToNextQuestion(currentAnswers);
                      }
                    }}
                  >
                    Next Question
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {currentView === "finished-review-modal" && <FinishedReviewModal />}

        <EditDpoDialog
          open={isEditDpoDialogOpen}
          onClose={() => setIsEditDpoDialogOpen(false)}
          onDone={async (values: DpoValues) => {
            await pcSettings.handleFormSubmit({
              dpoOfficerName: values.name,
              dpoOfficerEmail: values.email,
              dpoOfficerPhone: values.phone,
              dpoOfficerAddress: values.address,
            });
          }}
          values={{
            name: pcSettings?.dpoName,
            email: pcSettings?.dpoEmail,
            phone: pcSettings?.dpoPhone,
            address: pcSettings?.dpoAddress,
          }}
        />

        <EditEeaContactDialog
          open={isDesignatedRepDialogOpen}
          onClose={() => setIsDesignatedRepDialogOpen(false)}
          onDone={(values: EeaContactValues) => {
            pcSettings.handleFormSubmit({
              eeaContactName: values.eeaContactName,
              eeaContactAddress: values.eeaContactAddress,
              eeaContactEmail: values.eeaContactEmail,
              eeaContactPhone: values.eeaContactPhone,
            });
          }}
          values={{
            eeaContactName: pcSettings.eeaContactName,
            eeaContactAddress: pcSettings?.eeaContactAddress,
            eeaContactEmail: pcSettings?.eeaContactEmail,
            eeaContactPhone: pcSettings?.eeaContactPhone,
          }}
        />
      </div>
    </OmniBarProvider>
  );
}

function WelcomeMessage({
  currentView,
  isGdprRequired,
  onNextClick,
}: {
  currentView: CurrentView;
  isGdprRequired: boolean;
  onNextClick: () => void;
}) {
  if (currentView === "business-survey-welcome") {
    return (
      <div className="flex gap-[72px]">
        <div className="flex-1">
          <div className="text-[30px] font-bold text-netural-800 leading-[45px]">
            Great work! Let's build your business data profile with{" "}
            {isGdprRequired ? "three" : "two"} easy steps.
          </div>
          <div className="mt-[36px]">
            <img
              src={"/assets/images/get_compliant/business-survey-welcome.svg"}
              alt="Survey welcome"
              className="w-[208px] h-[175px]"
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="text-neutral-700 space-y-[24px]">
            <div>
              <div className="text-[24px] font-bold">1 - Business Survey</div>
              <div className="text-[20px] font-normal">General data handling practices</div>
            </div>
            {isGdprRequired && (
              <div>
                <div className="text-[24px] font-bold">2 - GDPR Survey</div>
                <div className="text-[20px] font-normal">Data handling in the EU/UK</div>
              </div>
            )}
            <div>
              <div className="text-[24px] font-bold">
                {isGdprRequired ? "3 - HR Survey" : "2 - HR Survey"}
              </div>
              <div className="text-[20px] font-normal">
                Employees and applicants data handling practices
              </div>
            </div>
          </div>
          <div className="mt-[50px]">
            <Button color="primary" onClick={onNextClick}>
              Take Me to the First Question
            </Button>
          </div>
        </div>
      </div>
    );
  }

  let imgSrcUrl = (() => {
    if (currentView === "eea-uk-survey-welcome")
      return "/assets/images/get_compliant/business_survey_gdpr_welcome.png";
  })();

  return (
    <div className="flex items-center" style={{ flexDirection: "column" }}>
      <div className="text-[30px] font-bold text-center text-netural-800 leading-[45px]">
        {currentView === "eea-uk-survey-welcome" && "Now let's review your GDPR data practices"}
      </div>
      <div className="mt-[36px]">
        <img src={imgSrcUrl} alt="Survey welcome" className="w-[300px] h-[300px]" />
      </div>
    </div>
  );
}

function CurrentSurveyIndicator({
  currentView,
  isGdprRequired,
}: {
  currentView: CurrentView;
  isGdprRequired: boolean;
}) {
  let isBusinessSurveyComplete = !(
    currentView === "business-survey-welcome" ||
    currentView === "business-survey" ||
    currentView === "consumer-collection-incentives-survey"
  );
  let isEeaUkSurveyComplete =
    !isGdprRequired ||
    (isBusinessSurveyComplete &&
      !(currentView === "eea-uk-survey-welcome" || currentView === "eea-uk-survey"));
  let isHrSurveyComplete = currentView === "finished-review-modal";

  let currentSurveyName = (() => {
    if (currentView === "pipeda-privacy-officer") return "PIPEDA Privacy Officer";
    if (!isBusinessSurveyComplete) return "Business Survey";
    if (!isEeaUkSurveyComplete && isGdprRequired) return "GDPR Survey";
    return "HR Survey";
  })();

  let emptyBackgroundStyleProps = {
    background: "#F3F0FE",
  };

  let filledBackgroundStyleProps = {
    background: "radial-gradient(141.69% 125.19% at 90.11% 100%, #3406CE 0%, #8378FF 100%)",
  };

  return (
    <div>
      <div className="text-[14px] text-neutral-800 font-medium">{currentSurveyName}</div>
      <div className="flex gap-[12px]">
        <div
          className="w-[250px] h-[12px] rounded-[999px]"
          style={isBusinessSurveyComplete ? filledBackgroundStyleProps : emptyBackgroundStyleProps}
        ></div>
        {isGdprRequired && (
          <div
            className="w-[250px] h-[12px] rounded-[999px]"
            style={isEeaUkSurveyComplete ? filledBackgroundStyleProps : emptyBackgroundStyleProps}
          ></div>
        )}
        <div
          className="w-[250px] h-[12px] rounded-[999px]"
          style={isHrSurveyComplete ? filledBackgroundStyleProps : emptyBackgroundStyleProps}
        ></div>
      </div>
    </div>
  );
}

function FinishedReviewModal() {
  const history = useHistory();
  return (
    <Modal
      title="Great work! Please review your answers and any flagged settings before continuing."
      alignText="center"
      firstButton="Review Your Answers"
      firstButtonColor="primary"
      onFirstButtonClick={() => {
        history.push(Routes.getCompliant.surveyAnswersReview);
      }}
      widthButton="full"
      showOverlay
      closeOnOverlayClick={false}
      closeButton={false}
    >
      <img
        src="/assets/images/get_compliant/review_survey_answers_modal.png"
        alt="Review survey answers"
        className="w-[234px] h-[211px] mt-[20px] mx-auto"
      />
    </Modal>
  );
}

export function SurveyQuestionSuccessMessage({
  messageText,
}: {
  messageText: string | React.ReactNode;
}) {
  return (
    <div className="bg-green-100 mt-[12px] max-w-[250px] mx-auto rounded-[6px] px-[12px] py-[7px] flex items-center gap-[14px]">
      <div>
        <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
      </div>
      <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
        {messageText}
      </div>
    </div>
  );
}

export function initialViewAndQuestionSlug(
  org: OrganizationDto,
  {
    bizAnswers,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  }: {
    bizAnswers: SurveyAnswers;
    incentivesAnswers: SurveyAnswers;
    hrAnswers: SurveyAnswers;
    eeaAnswers: SurveyAnswers;
  },
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
  allFlaggedForReviewSlugs: string[],
): [CurrentView, string] {
  const flags = {
    isGdprRequired: org?.featureGdpr,
    dpoRequired: org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter),
    hasDpo: Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail),
    hasDesignatedRep: hasDesignatedRepresentative(pcSettings),
  };

  let {
    bizQuestionsWithVisibility,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
    bizAnswers,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  });

  let bizSurveyVisibleQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
  let incentivesSurveyVisibleQuestions = incentivesQuestionsWithVisibility.filter((q) => q.visible);
  let hrVisibileQuestions = hrQuestionsWithVisibility.filter((q) => q.visible);
  let eeaVisibileQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);

  let numBizSurveyAnswered = bizSurveyVisibleQuestions.filter(
    (q) =>
      hasAnswered(bizAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numIncentivesSurveyAnswered = incentivesSurveyVisibleQuestions.filter(
    (q) =>
      hasAnswered(incentivesAnswers, q, /* debug = */ false) ||
      allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numHrAnswered = hrVisibileQuestions.filter(
    (q) =>
      hasAnswered(hrAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numEeaAnswered = eeaVisibileQuestions.filter(
    (q) =>
      hasAnswered(eeaAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;

  if (numBizSurveyAnswered == 0) {
    return ["business-survey-welcome", null];
  } else if (numBizSurveyAnswered < bizSurveyVisibleQuestions.length) {
    let firstUnansweredBizQuestionSlug = bizSurveyVisibleQuestions.find(
      (q) =>
        !hasAnswered(bizAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["business-survey", firstUnansweredBizQuestionSlug];
  } else if (numIncentivesSurveyAnswered < incentivesSurveyVisibleQuestions.length) {
    let firstUnansweredIncentivesQuestionSlug = incentivesSurveyVisibleQuestions.find(
      (q) =>
        !hasAnswered(incentivesAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["consumer-collection-incentives-survey", firstUnansweredIncentivesQuestionSlug];
  } else if (numEeaAnswered == 0 && org?.featureGdpr) {
    return ["eea-uk-survey-welcome", null];
  } else if (numEeaAnswered < eeaVisibileQuestions.length && flags.isGdprRequired) {
    let firstUnansweredEeaQuestionSlug = eeaVisibileQuestions.find(
      (q) =>
        // For the DPO question, it is considered unanswered if a DPO is required, and the user has selected "Yes" but hasn't actually entered DPO details
        (q.slug === HAS_DATA_PRIVACY_OFFICER_SLUG &&
          flags.dpoRequired &&
          eeaAnswers[q.slug] === "true" &&
          !flags.hasDpo) ||
        // For the Designated Rep question, it is considered unanswered if the user selected "Yes" but hasn't actually entered Designated Representative details
        (q.slug === HAS_DESIGNATED_REP_SLUG &&
          eeaAnswers[q.slug] === "true" &&
          !flags.hasDesignatedRep) ||
        !hasAnswered(eeaAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["eea-uk-survey", firstUnansweredEeaQuestionSlug];
  } else if (org.featurePipeda && !(pcSettings.pipedaOfficerName?.length > 0)) {
    return ["pipeda-privacy-officer", null];
  } else if (numHrAnswered < hrVisibileQuestions.length) {
    let firstUnansweredHrQuestionSlug = hrVisibileQuestions.find(
      (q) =>
        !hasAnswered(hrAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["hr-survey", firstUnansweredHrQuestionSlug];
  } else {
    return ["finished-review-modal", null];
  }
}

export function allSurveyQuestionsWithVisibility(
  org: OrganizationDto,
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
  {
    bizAnswers,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  }: {
    bizAnswers: SurveyAnswers;
    incentivesAnswers: SurveyAnswers;
    hrAnswers: SurveyAnswers;
    eeaAnswers: SurveyAnswers;
  },
): {
  bizQuestionsWithVisibility: QuestionWithVisibility[];
  incentivesQuestionsWithVisibility: QuestionWithVisibility[];
  hrQuestionsWithVisibility: QuestionWithVisibility[];
  eeaQuestionsWithVisibility: QuestionWithVisibility[];
} {
  let bizSurveyDefinition = businessSurvey(bizAnswers);
  let bizQuestionsWithVisibility = questionsWithVisibility(bizSurveyDefinition, bizAnswers);

  let incentivesSurveyDefinition = consumerCollectionIncentivesSurvey(incentivesAnswers, false);
  let incentivesQuestionsWithVisibility = questionsWithVisibility(
    incentivesSurveyDefinition,
    incentivesAnswers,
  ).filter((q) => q.slug === OFFERS_CONSUMER_INCENTIVES_SLUG);

  let hrDefinition = hrSurvey(hrAnswers, org?.featureGdpr);
  let hrQuestionsWithVisibility = questionsWithVisibility(hrDefinition, hrAnswers);

  let eeaDefinition = eeaUkSurvey(eeaAnswers, privacyCenter, pcSettings);
  let eeaQuestionsWithVisibility = questionsWithVisibility(eeaDefinition, eeaAnswers);

  return {
    bizQuestionsWithVisibility,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  };
}
